import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // 업적 API 에러는 무시 (아직 구현되지 않을 수 있음)
      if (error.config?.url?.includes('/achievement/')) {
        return Promise.reject(error);
      }
      
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      // 페이지 새로고침으로 로그인 페이지로 이동
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  timestamp: string;
}

// Generic API methods
export const api = {
  get: <T>(url: string): Promise<ApiResponse<T>> =>
    apiClient.get(url).then((response) => response.data),
  
  post: <T>(url: string, data?: any): Promise<ApiResponse<T>> =>
    apiClient.post(url, data).then((response) => response.data),
  
  put: <T>(url: string, data?: any): Promise<ApiResponse<T>> =>
    apiClient.put(url, data).then((response) => response.data),
  
  delete: <T>(url: string): Promise<ApiResponse<T>> =>
    apiClient.delete(url).then((response) => response.data),
};

// Health check function
export const healthCheck = async (): Promise<boolean> => {
  try {
    const response = await api.get('/health');
    return response.success;
  } catch (error) {
    console.error('Health check failed:', error);
    return false;
  }
};

// User API functions
export const userApi = {
  // Get user profile
  getProfile: () => 
    api.get('/user/profile'),
  
  // Update user profile
  updateProfile: (data: any) => 
    api.put('/user/profile', data),
  
  // Get current active session
  getCurrentSession: () => 
    api.get('/user/current-session'),
  
  // Get user exam history
  getHistory: (options?: { limit?: number; offset?: number; examType?: string }) => {
    const queryParams = new URLSearchParams();
    if (options?.limit) queryParams.append('limit', options.limit.toString());
    if (options?.offset) queryParams.append('offset', options.offset.toString());
    if (options?.examType) queryParams.append('examType', options.examType);
    
    const queryString = queryParams.toString();
    return api.get(`/user/history${queryString ? '?' + queryString : ''}`);
  },
  
  // Get user statistics
  getStatistics: () => 
    api.get('/user/statistics'),
  
  // Update user paid plan
  updatePaidPlan: (paid_plan: number) => 
    api.put('/user/paid-plan', { paid_plan }),
};

// Achievement API functions
export const achievementApi = {
  // Get user achievements
  getMyAchievements: () => 
    api.get('/achievement/my-achievements'),
  
  // Get achievement progress
  getProgress: () => 
    api.get('/achievement/progress'),
};

// Exam API functions
export const examApi = {
  // Get exam types (with access restrictions)
  getTypes: () => 
    api.get('/exam/types'),
  
  // Get all exam types (for results display, no access restrictions)
  getAllTypes: () => 
    api.get('/exam/types/all'),
  
  // Start new exam
  start: (examType: string, mode: 'practice' | 'exam' = 'exam') => 
    api.post('/exam/start', { examType, mode }),
  
  // Get question
  getQuestion: (sessionId: string, questionNumber: number) => 
    api.get(`/exam/${sessionId}/question/${questionNumber}`),
  
  // Submit answer
  submitAnswer: (sessionId: string, questionNumber: number, selectedAnswers: number[]) => 
    api.post(`/exam/${sessionId}/answer`, { questionNumber, selectedAnswers }),
  
  // Get exam progress
  getProgress: (sessionId: string) => 
    api.get(`/exam/${sessionId}/progress`),
  
  // Submit exam
  submit: (sessionId: string) => 
    api.post(`/exam/${sessionId}/submit`),
  
  // Get results
  getResults: (sessionId: string) => 
    api.get(`/exam/${sessionId}/results`),
  
  // Abandon exam
  abandon: (sessionId: string) => 
    api.delete(`/exam/${sessionId}`),
  
  // Pause exam
  pause: (sessionId: string) => 
    api.post(`/exam/${sessionId}/pause`),
  
  // Resume exam
  resume: (sessionId: string) => 
    api.post(`/exam/${sessionId}/resume`),

  // Keep exam session active
  touchActivity: (sessionId: string) =>
    api.post(`/exam/${sessionId}/activity`),
  
  // Toggle question flag
  toggleFlag: (sessionId: string, questionNumber: number) => 
    api.post(`/exam/${sessionId}/flag/${questionNumber}`),
};

// Admin API functions
export const adminApi = {
  // Batch analyze questions with AI
  batchAnalyzeQuestions: (examType: string, options: { limit?: number; startFromId?: string; fixAnswers?: boolean }) => 
    api.post(`/admin/questions/${examType}/analyze-batch`, options),
  
  // Get questions
  getQuestions: (examType: string, options?: { limit?: number; offset?: number; questionId?: string; optionCount?: string; validated?: string }) => {
    const queryParams = new URLSearchParams({ examType });
    if (options?.limit) queryParams.append('limit', options.limit.toString());
    if (options?.offset) queryParams.append('offset', options.offset.toString());
    if (options?.questionId) queryParams.append('questionId', options.questionId);
    if (options?.optionCount) queryParams.append('optionCount', options.optionCount);
    if (options?.validated) queryParams.append('validated', options.validated);
    
    return api.get(`/admin/questions?${queryParams.toString()}`);
  },
};

export default apiClient;
